import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import {
  getCourse, addModule, updateModule, deleteModule,
  addTopic, updateTopic, deleteTopic
} from '../../services/lmsService';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SimpleRichTextEditor from '../ui/rich-text-editor';
import { toast } from 'sonner';
import Footer from '../Footer';
import {
  ArrowLeft, Plus, Pencil, Trash2, GripVertical,
  ChevronDown, ChevronRight, BookOpen, FileText,
  Video, PlayCircle, FileQuestion, Clock, Save, RefreshCw
} from 'lucide-react';

const CourseContentManager = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({ title: '', description: '', estimated_hours: 1 });
  
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [currentModuleId, setCurrentModuleId] = useState(null);
  const [topicForm, setTopicForm] = useState({ title: '', description: '', content_type: 'lecture', estimated_duration_minutes: 30, content_url: '', is_free_preview: false });
  
  useEffect(() => {
    // Allow both admin and sub_admin roles
    if (!user || (user.lms_role !== 'admin' && user.lms_role !== 'sub_admin')) { navigate('/'); return; }
    fetchCourse();
  }, [courseId, user]);
  
  const fetchCourse = async () => {
    try {
      setLoading(true);
      const response = await getCourse(courseId);
      if (response.success) {
        setCourse(response.data);
        const expanded = {};
        response.data.syllabus?.forEach(m => expanded[m.id] = true);
        setExpandedModules(expanded);
      }
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/lms-admin');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleModule = (moduleId) => setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  
  const openAddModule = () => { setEditingModule(null); setModuleForm({ title: '', description: '', estimated_hours: 1 }); setShowModuleModal(true); };
  const openEditModule = (module) => { setEditingModule(module); setModuleForm({ title: module.title, description: module.description || '', estimated_hours: module.estimated_hours || 1 }); setShowModuleModal(true); };
  
  const handleSaveModule = async () => {
    try {
      if (editingModule) { await updateModule(editingModule.id, moduleForm); toast.success('Module updated'); }
      else { await addModule(courseId, moduleForm); toast.success('Module added'); }
      setShowModuleModal(false); fetchCourse();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save module'); }
  };
  
  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and all its topics?')) return;
    try { await deleteModule(moduleId); toast.success('Module deleted'); fetchCourse(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete module'); }
  };
  
  const openAddTopic = (moduleId) => { setCurrentModuleId(moduleId); setEditingTopic(null); setTopicForm({ title: '', description: '', content_type: 'lecture', estimated_duration_minutes: 30, content_url: '', is_free_preview: false }); setShowTopicModal(true); };
  const openEditTopic = (moduleId, topic) => { setCurrentModuleId(moduleId); setEditingTopic(topic); setTopicForm({ title: topic.title, description: topic.description || '', content_type: topic.content_type || 'lecture', estimated_duration_minutes: topic.estimated_duration_minutes || 30, content_url: topic.content_url || '', is_free_preview: topic.is_free_preview || false }); setShowTopicModal(true); };
  
  const handleSaveTopic = async () => {
    try {
      if (editingTopic) { await updateTopic(editingTopic.id, topicForm); toast.success('Topic updated'); }
      else { await addTopic(currentModuleId, topicForm); toast.success('Topic added'); }
      setShowTopicModal(false); fetchCourse();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save topic'); }
  };
  
  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Delete this topic?')) return;
    try { await deleteTopic(topicId); toast.success('Topic deleted'); fetchCourse(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete topic'); }
  };
  
  const getContentTypeIcon = (type) => {
    const icons = { lecture: <Video className="w-4 h-4" />, video: <PlayCircle className="w-4 h-4" />, assignment: <FileText className="w-4 h-4" />, quiz: <FileQuestion className="w-4 h-4" /> };
    return icons[type] || <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/lms-admin')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
          </Button>
          
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{course.title}</h1>
            <p className="text-gray-600 text-sm">Manage course content - modules and topics</p>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Course Syllabus</h2>
              <p className="text-gray-500 text-sm">{course.syllabus?.length || 0} modules</p>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto" onClick={openAddModule}>
              <Plus className="w-4 h-4 mr-2" />Add Module
            </Button>
          </div>
          
          {/* Modules List */}
          <div className="space-y-4">
            {course.syllabus?.length === 0 ? (
              <Card className="text-center py-8 sm:py-12">
                <CardContent>
                  <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-600 mb-2">No modules yet</h3>
                  <p className="text-gray-500 text-sm mb-4">Start building your course by adding modules</p>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={openAddModule}><Plus className="w-4 h-4 mr-2" />Add First Module</Button>
                </CardContent>
              </Card>
            ) : (
              course.syllabus?.map((module, index) => (
                <Card key={module.id} className="overflow-hidden">
                  <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => toggleModule(module.id)}>
                    <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hidden sm:block" />
                    <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs sm:text-sm">{index + 1}</span>
                    {expandedModules[module.id] ? <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{module.title}</h3>
                      <p className="text-xs sm:text-sm text-gray-500">{module.topics?.length || 0} topics{module.estimated_hours && ` • ${module.estimated_hours}h`}</p>
                    </div>
                    <div className="flex gap-1 sm:gap-2" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => openEditModule(module)}><Pencil className="w-4 h-4" /></Button>
                      {user.lms_role === 'admin' && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteModule(module.id)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                  
                  {expandedModules[module.id] && (
                    <CardContent className="border-t p-3 sm:p-4">
                      {module.description && <p className="text-gray-600 text-xs sm:text-sm mb-4">{module.description}</p>}
                      <div className="space-y-2">
                        {module.topics?.map((topic, topicIndex) => (
                          <div key={topic.id} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white border rounded-lg hover:shadow-sm">
                            <span className="text-xs sm:text-sm text-gray-400 w-5 sm:w-6">{topicIndex + 1}.</span>
                            {getContentTypeIcon(topic.content_type)}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 text-xs sm:text-sm truncate">{topic.title}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Badge variant="outline" className="capitalize text-xs">{topic.content_type}</Badge>
                                {topic.estimated_duration_minutes && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{topic.estimated_duration_minutes}m</span>}
                                {topic.is_free_preview && <Badge className="bg-green-100 text-green-700 text-xs">Free</Badge>}
                              </div>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => openEditTopic(module.id, topic)}><Pencil className="w-4 h-4" /></Button>
                            {user.lms_role === 'admin' && (
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteTopic(topic.id)}><Trash2 className="w-4 h-4" /></Button>
                            )}
                          </div>
                        ))}
                        <Button variant="outline" className="w-full mt-2 border-dashed text-xs sm:text-sm" onClick={() => openAddTopic(module.id)}><Plus className="w-4 h-4 mr-2" />Add Topic</Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* Module Modal */}
      <Dialog open={showModuleModal} onOpenChange={setShowModuleModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingModule ? 'Edit Module' : 'Add Module'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={moduleForm.title} onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })} placeholder="Module title" /></div>
            <div className="space-y-2"><Label>Description</Label><SimpleRichTextEditor value={moduleForm.description} onChange={(val) => setModuleForm({ ...moduleForm, description: val })} placeholder="Module overview, learning objectives, key topics..." compact={true} /></div>
            <div className="space-y-2"><Label>Estimated Hours</Label><Input type="number" min={0.5} step={0.5} value={moduleForm.estimated_hours} onChange={(e) => setModuleForm({ ...moduleForm, estimated_hours: parseFloat(e.target.value) })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModuleModal(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveModule} disabled={!moduleForm.title}><Save className="w-4 h-4 mr-2" />{editingModule ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Topic Modal */}
      <Dialog open={showTopicModal} onOpenChange={setShowTopicModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingTopic ? 'Edit Topic' : 'Add Topic'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={topicForm.title} onChange={(e) => setTopicForm({ ...topicForm, title: e.target.value })} placeholder="Topic title" /></div>
            <div className="space-y-2"><Label>Description</Label><SimpleRichTextEditor value={topicForm.description} onChange={(val) => setTopicForm({ ...topicForm, description: val })} placeholder="What will be covered, key takeaways, resources..." compact={true} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Type</Label><Select value={topicForm.content_type} onValueChange={(v) => setTopicForm({ ...topicForm, content_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="lecture">Live Lecture</SelectItem><SelectItem value="video">Recorded</SelectItem><SelectItem value="assignment">Assignment</SelectItem><SelectItem value="quiz">Quiz</SelectItem><SelectItem value="reading">Reading</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Duration (min)</Label><Input type="number" min={5} value={topicForm.estimated_duration_minutes} onChange={(e) => setTopicForm({ ...topicForm, estimated_duration_minutes: parseInt(e.target.value) })} /></div>
            </div>
            <div className="space-y-2"><Label>Content URL</Label><Input value={topicForm.content_url} onChange={(e) => setTopicForm({ ...topicForm, content_url: e.target.value })} placeholder="https://..." /></div>
            <div className="flex items-center gap-2"><input type="checkbox" id="freePreview" checked={topicForm.is_free_preview} onChange={(e) => setTopicForm({ ...topicForm, is_free_preview: e.target.checked })} className="rounded" /><Label htmlFor="freePreview">Free Preview</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTopicModal(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveTopic} disabled={!topicForm.title}><Save className="w-4 h-4 mr-2" />{editingTopic ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default CourseContentManager;
